import chamberDefaultPlate from '@/assets/images/dice/ritual-chamber.webp'
import chamberDeathPlate from '@/assets/images/dice/ritual-chamber-death.webp'
import deathDieSurface from '@/assets/images/dice/die-surface-death.webp'

/**
 * A dice theme is the whole look of one roll: the room it happens in, the light
 * in that room, and what the die is made of. Everything the roller draws reads
 * its colours from here, so a theme is added by adding an entry — not by
 * hunting colours through shaders, canvases and stylesheets.
 */
export const DICE_THEMES = {
    default: {
        id: 'default',
        plate: chamberDefaultPlate,

        /**
         * Where the ring sits inside the plate and how wide it is, as fractions
         * of the plate. The die is drawn into this box, so a new plate declares
         * its own geometry here rather than being forced to match a fixed one.
         */
        /**
         * `lift` is how far the die's box is raised over the ring centre, as a
         * percentage of its own height — it absorbs the difference in camera
         * pitch between one plate's photograph and another's.
         */
        ring: { x: 0.51, y: 0.59, size: 0.34, lift: 58 },

        /** The plate's own aspect, so it is covered without being distorted. */
        plateRatio: 1537 / 1023,

        /** Painted into every face canvas by diceFaceTexture. */
        face: {
            stoneCore: '#141c24',
            stoneEdge: '#05080b',
            accent: '#7fe4ff',
            grain: '120, 160, 190',
            vein: '127, 228, 255',
            critSuccess: '#ffd27f',
            critFail: '#ff5a4a',
            critNeutral: '#c9f4ff',
            numeralEdge: 'rgba(255, 255, 255, 0.55)',
            /** A rendered material plate; null means the painted stone. */
            surface: null
        },

        material: 'obsidian',

        lighting: {
            ambient: 0x1a2a34,
            ambientIntensity: 0.5,
            hemiSky: 0x39647a,
            hemiGround: 0x02060a,
            hemiIntensity: 0.4,
            key: 0xbdefff,
            keyIntensity: 70,
            rim: 0x8fd0ea,
            rimIntensity: 0.7
        },

        environment: {
            ringLight: 0x7fe4ff,
            ringLightIntensity: 9,
            contactGlow: ['rgba(180, 236, 255, 0.42)', 'rgba(90, 170, 210, 0.14)']
        },

        fog: { color: 0x9fd8f0, intensity: 1, opacity: 0.85 },

        /** Bound onto the chamber element; the stylesheet only ever reads these. */
        css: {
            '--flow': '#7fe4ff',
            '--flow-dim': '#5d94ad',
            '--flow-rgb': '127, 228, 255',
            '--ash': '#93a8b3',
            '--crit': '#ffd27f',
            '--bane': '#ff5a4a',
            '--bane-rgb': '255, 90, 74',
            '--inscription': '#cdeffc',
            '--cast-face': '#dff6ff'
        }
    },

    /**
     * A death save is not the same room. The chamber gives way to a grave yard
     * in a dead valley, and the Flow in the ring burns grave-green — the die
     * with it, blackened iron with the same light in its cracks.
     */
    death: {
        id: 'death',
        plate: chamberDeathPlate,

        ring: { x: 0.506, y: 0.607, size: 0.340, lift: 52 },

        plateRatio: 3344 / 1882,

        face: {
            stoneCore: '#101a14',
            stoneEdge: '#030605',
            accent: '#79f2a6',
            grain: '120, 175, 140',
            vein: '121, 242, 166',
            critSuccess: '#d8f77a',
            critFail: '#ff4d55',
            critNeutral: '#b6ffd2',
            numeralEdge: 'rgba(214, 255, 232, 0.5)',
            surface: deathDieSurface
        },

        material: 'blackIron',

        lighting: {
            ambient: 0x14261c,
            ambientIntensity: 0.45,
            hemiSky: 0x2f7a52,
            hemiGround: 0x030806,
            hemiIntensity: 0.38,
            key: 0xd8ffe8,
            keyIntensity: 58,
            rim: 0x5fd692,
            rimIntensity: 0.6
        },

        environment: {
            ringLight: 0x4be08c,
            ringLightIntensity: 8,
            contactGlow: ['rgba(140, 245, 185, 0.40)', 'rgba(50, 150, 100, 0.14)']
        },

        fog: { color: 0x8fd8ab, intensity: 0.9, opacity: 0.78 },

        css: {
            '--flow': '#79f2a6',
            '--flow-dim': '#3f7d5c',
            '--flow-rgb': '121, 242, 166',
            '--ash': '#96b3a3',
            '--crit': '#d8f77a',
            '--bane': '#ff4d55',
            '--bane-rgb': '255, 77, 85',
            '--inscription': '#cdfce0',
            '--cast-face': '#e2fff0'
        }
    }
}

export const DEFAULT_DICE_THEME = 'default'

export const getDiceTheme = (id) => DICE_THEMES[id] || DICE_THEMES[DEFAULT_DICE_THEME]

export const diceThemeIds = () => Object.keys(DICE_THEMES)

export default DICE_THEMES
